<template>
  <v-navigation-drawer
    :model-value="modelValue"
    :location="drawerLocation"
    :width="drawerWidth"
    :rounded="!mdAndUp ? 't-xl' : '0'"
    :order="-1"
    :aria-label="ariaLabel || undefined"
    aria-modal="true"
    class="bm-drawer-sheet"
    role="dialog"
    tabindex="-1"
    temporary
    data-cc="bm-drawer-sheet"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <div class="d-flex flex-column h-100">
      <div v-if="$slots.header" data-cc="bm-drawer-sheet-header">
        <slot name="header" />
        <v-divider />
      </div>

      <div
        class="flex-1-1 overflow-y-auto styled-scrollbar"
        data-cc="bm-drawer-sheet-content"
      >
        <slot />
      </div>

      <div v-if="$slots.footer" data-cc="bm-drawer-sheet-footer">
        <v-divider />
        <slot name="footer" />
      </div>
    </div>
  </v-navigation-drawer>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import { useDisplay } from 'vuetify';

interface Props {
  modelValue: boolean;
  location?: 'right' | 'left';
  width?: number | string;
  ariaLabel?: string;
}

const props = withDefaults(defineProps<Props>(), {
  location: 'right',
  width: 420,
  ariaLabel: ''
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
}>();

const { mdAndUp } = useDisplay();

const drawerLocation = computed(() =>
  mdAndUp.value ? props.location : 'bottom'
);

/**
 * Vuetify's VNavigationDrawer runs `Number(width)` internally. Values like
 * `'100%'` or `'100vw'` become `NaN` and break the bottom drawer's layout
 * and transform calculations (the drawer slides from the right instead of
 * the bottom). Passing an empty string lets Vuetify fall back to its own
 * default sizing for `location="bottom"`.
 *
 * On desktop the consumer-supplied `width` prop is forwarded as-is.
 *
 * Vuetify 3.12.x has no built-in fix — revisit if a future version handles
 * bottom drawers outside of `v-layout` correctly.
 */
const drawerWidth = computed(() => (mdAndUp.value ? props.width : ''));
</script>

<style lang="scss">
.bm-drawer-sheet {
  &.v-navigation-drawer {
    z-index: 1100 !important;

    + .v-navigation-drawer__scrim {
      z-index: 1099 !important;
    }

    &:not(.v-navigation-drawer--bottom) {
      top: 0 !important;
      height: 100dvh !important;
      max-height: 100dvh !important;
    }

    &--left {
      left: 0 !important;
      right: auto !important;
    }

    &--bottom {
      &.v-navigation-drawer--active {
        height: calc(100dvh - 48px) !important;
        max-height: calc(100dvh - 48px) !important;
      }
    }
  }
}
</style>
